import { useContext, useEffect, useState } from 'react'

import { useAnalysisDetailMutation } from '@/common/api/modules/article'
import { KeywordAnalysis, KeywordItem } from '@/common/model/media/articleCreator'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Icon, { IconType } from '@/components/view/atoms/icon/Icon'
import LoadingObject, { LoadingObjectType } from '@/components/view/atoms/loading/LoadingObject'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import AiInsideArea from '@/components/view/layouts/area/AiInsideArea'
import Modal, { ModalType } from '@/components/view/layouts/modal/Modal'
import ModalContent, { ModalContentType } from '@/components/view/layouts/modal/ModalContent'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'
import ModalHeader from '@/components/view/molecules/modal/ModalHeader'

import KeywordAnalysisTable from '@/components/container/modules/media/articleCreator/KeywordAnalysisTable'

import { KeywordHistoryResultModalContext } from './KeywordHistoryResultModalProvider'

interface ContentProps {
  siteId: number
  historyId: number
  onApply(keyword: string): void
  onClose(): void
}

// モーダルが開くたびに再マウントし、選択状態を初期化する
const KeywordHistoryResultModalContent = ({
  siteId,
  historyId,
  onApply,
  onClose
}: ContentProps) => {
  const [selected, setSelected] = useState<KeywordItem[]>([])
  const { trigger: fetchDetail, data, isMutating: loading } = useAnalysisDetailMutation()

  useEffect(() => {
    if (historyId != null) {
      fetchDetail({ site_id: siteId, history_id: historyId }).catch(() => undefined)
    }
    // マウント時に一度だけ取得する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const analysis = data?.result_data as KeywordAnalysis | undefined
  const inputKeyword = (data?.input_data?.keywords as string[] | undefined)?.join(', ') ?? ''

  const handleApply = () => {
    if (selected.length === 0) return
    onApply(selected.map((k) => k.keyword).join(', '))
    onClose()
  }

  return (
    <>
      <ModalContent type={ModalContentType.NO_FRAME}>
        {loading ? (
          <div className='flex flex-col items-center gap-3 p-8'>
            <LoadingObject type={LoadingObjectType.TEXT} />
            <Text size={14} color={TextColor.SECONDARY}>
              分析結果を読み込んでいます...
            </Text>
          </div>
        ) : analysis?.summary?.best_opportunity ? (
          <>
            <div className='p-2'>
              <AiInsideArea border>
                <div className='flex items-start p-3'>
                  <Icon type={IconType.AI_FILL} />
                  <Text size={14} color={TextColor.AI}>
                    {analysis.summary.recommendation}
                  </Text>
                </div>
              </AiInsideArea>
            </div>
            {analysis.keywords && analysis.keywords.length > 0 ? (
              <KeywordAnalysisTable
                keywords={analysis.keywords}
                inputKeyword={inputKeyword}
                selectedKeywords={selected}
                onSelect={setSelected}
              />
            ) : (
              <Text size={14} color={TextColor.SECONDARY} align='center'>
                キーワード候補がありません
              </Text>
            )}
          </>
        ) : analysis?.keywords && analysis.keywords.length > 0 ? (
          <KeywordAnalysisTable
            keywords={analysis.keywords}
            inputKeyword={inputKeyword}
            selectedKeywords={selected}
            onSelect={setSelected}
          />
        ) : (
          <Text size={14} color={TextColor.SECONDARY} align='center'>
            分析結果が見つかりませんでした
          </Text>
        )}
      </ModalContent>
      <ModalFooter>
        <Button color={ButtonColor.SECONDARY} size='large' onClick={onClose}>
          閉じる
        </Button>
        <Button
          color={ButtonColor.PRIMARY}
          size='large'
          disabled={selected.length === 0}
          onClick={handleApply}
        >
          選択したキーワードを反映する
        </Button>
      </ModalFooter>
    </>
  )
}

// 過去のキーワード分析結果を表示するモーダル（分析履歴の「分析結果を見る」から起動）
const KeywordHistoryResultModal = () => {
  const { modal, showing, hide } = useContext(KeywordHistoryResultModalContext)

  return (
    <Modal show={showing} type={ModalType.MIDDLE}>
      <ModalHeader text='キーワード分析結果' onClickClose={hide} />
      {modal.showing && modal.historyId != null && (
        <KeywordHistoryResultModalContent
          siteId={modal.siteId}
          historyId={modal.historyId}
          onApply={modal.onApply}
          onClose={hide}
        />
      )}
    </Modal>
  )
}

export default KeywordHistoryResultModal
